import { atom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';

import { DEFAULT_TOOL, DevTool } from '@/features/developer-tools/components/dev-tools.constants';
import { generateUUID } from '@/utils/uuid';

/**
 * Registry of all open panel instance IDs
 */
export const devToolsPanelInstancesAtom = atom<string[]>([]);
devToolsPanelInstancesAtom.debugLabel = 'devToolsPanelInstances';

/**
 * Per-instance active item state with sessionStorage persistence
 */
const sessionStorage = createJSONStorage<DevTool>(() => globalThis.sessionStorage);

export const devToolsActiveItemAtom = atomFamily((instanceId: string) => {
  const itemAtom = atomWithStorage<DevTool>(
    `dev-tools-item-${instanceId}`,
    DEFAULT_TOOL,
    sessionStorage
  );
  itemAtom.debugLabel = `devToolsActiveItem-${instanceId}`;
  return itemAtom;
});

/**
 * Per-instance open state
 */
export const devToolsPanelOpenFamily = atomFamily((instanceId: string) => {
  const openAtom = atom(true); // Panels start open when created
  openAtom.debugLabel = `devToolsPanelOpen-${instanceId}`;
  return openAtom;
});

/**
 * Create a new panel instance and add it to the registry
 */
export const createPanelAtom = atom(null, (get, set, initialTab?: DevTool) => {
  const instanceId = generateUUID();
  const instances = get(devToolsPanelInstancesAtom);

  // Add to registry
  set(devToolsPanelInstancesAtom, [...instances, instanceId]);

  // Set initial tab if provided
  if (initialTab) {
    set(devToolsActiveItemAtom(instanceId), initialTab);
  }

  return instanceId;
});
createPanelAtom.debugLabel = 'createPanel';

/**
 * Remove a panel instance from the registry
 */
export const removePanelAtom = atom(null, (get, set, instanceId: string) => {
  const instances = get(devToolsPanelInstancesAtom);
  set(
    devToolsPanelInstancesAtom,
    instances.filter((id) => id !== instanceId)
  );

  // Clean up sessionStorage
  try {
    sessionStorage.removeItem?.(`dev-tools-item-${instanceId}`);
  } catch {
    // Ignore storage errors
  }
});
removePanelAtom.debugLabel = 'removePanel';

/**
 * Open or switch to a specific item on the topmost panel (or create a new panel if none exist)
 */
export const openToItemAtom = atom(null, (get, set, item: DevTool) => {
  const instances = get(devToolsPanelInstancesAtom);

  if (instances.length === 0) {
    // Create new panel with the target item
    set(createPanelAtom, item);
  } else {
    // Switch the topmost panel to the target item
    const topmostInstanceId = instances[instances.length - 1];
    set(devToolsActiveItemAtom(topmostInstanceId), item);
  }
});
openToItemAtom.debugLabel = 'openToItem';

/**
 * Close panel instances whose active item matches (sets open false; DevToolsPanel removes them).
 */
export const closeItemAtom = atom(null, (get, set, item: DevTool) => {
  const instances = get(devToolsPanelInstancesAtom);

  for (const instanceId of instances) {
    if (get(devToolsActiveItemAtom(instanceId)) !== item) continue;
    set(devToolsPanelOpenFamily(instanceId), false);
  }
});
closeItemAtom.debugLabel = 'closeItem';
